import Cookies from "js-cookie";
import { jwtDecode } from "jwt-decode";
import { getShowFullProfileFields } from "../utils/careerJourneyProfileFields";
import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useMemo,
} from "react";

const ProfileContext = createContext();

export const useProfile = () => {
  const context = useContext(ProfileContext);
  if (!context) {
    throw new Error("useProfile must be used within a ProfileProvider");
  }
  return context;
};

export const ProfileProvider = ({ children }) => {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [functions, setFunctions] = useState([]);
  const [countries, setCountries] = useState([]);
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [hasProfileChanges, setHasProfileChanges] = useState(false);
  const [hasExistingProfile, setHasExistingProfile] = useState(false);

  // Helper function to normalize profile data structure
  const normalizeProfileData = useCallback((data) => {
    if (!data) return null;

    // Handle if data is an array (profile[0] case)
    const profileData = Array.isArray(data) ? data[0] : data;
    console.log("profileData", profileData);
    const personalInfo = profileData
      ? {
          firstName: profileData?.first_name || profileData?.firstName || "",
          lastName: profileData?.last_name || profileData?.lastName || "",
          email: profileData?.email || "",
          isAge18OrAbove: Boolean(
            profileData?.is_age_18_or_above ||
            profileData?.isAge18OrAbove ||
            false,
          ),
          country: profileData?.country_code || profileData?.country || "",
          // city: profileData.city || "",
          workEligibleCountries:
            (profileData?.workEligibleCountries || []).includes("CH") ||
            (profileData?.workEligibleCountries || []).length === 0
              ? ["CH"]
              : ["CH"], // Always ensure Switzerland is the only one
          workEligibleSwiss: profileData?.workEligibleSwiss || [],
          jobSearchStatus:
            profileData?.job_search_status ||
            profileData?.jobSearchStatus ||
            "passive",
          function: profileData?.function || "",
          preferredCompanyTypes: profileData?.preferredCompanyTypes || [],
          preferredWorkEnvironments:
            profileData?.preferredWorkEnvironments || [],
          noticePeriod: profileData?.noticePeriod || {
            value: 7,
            unit: "days",
            immediate: false,
          },
        }
      : null;
    return {
      personal_info: personalInfo,
      career_details:
        profileData?.career_details || profileData?.careerDetails || null,
      preferred_roles:
        profileData?.preferred_roles || profileData?.preferredRoles || null,
      softSkills: profileData?.softSkills || profileData?.soft_skills || [],
      languages: profileData?.languages || [],
      roleExpertise:
        profileData?.roleExpertise || profileData?.role_expertise || null,
      has_career_journey_created: Boolean(profileData?.has_career_journey_created),
    };
  }, []);

  // Temporary profile data for multi-step profile creation
  const [tempProfileData, setTempProfileData] = useState(() => {
    try {
      const saved = localStorage.getItem("tempProfileData");

      if (!saved) {
        return {
          personalInfo: null,
          careerDetails: null,
          preferredRoles: null,
          roleExpertise: null,
          softSkills: [],
          languages: [],
        };
      }

      const parsed = JSON.parse(saved);
      console.log("parsed", parsed);

      // Normalize the structure
      return {
        personalInfo: parsed?.personalInfo || null,
        careerDetails: parsed?.careerDetails || null,
        preferredRoles: parsed?.preferredRoles || null,
        roleExpertise: parsed?.roleExpertise || null,
        softSkills: parsed?.softSkills || [],
        languages: parsed?.languages || [],
      };
    } catch (error) {
      return {
        personalInfo: null,
        careerDetails: null,
        preferredRoles: null,
        roleExpertise: null,
        softSkills: [],
        languages: [],
      };
    }
  });

  const [profileReviewStatus, setProfileReviewStatus] = useState(null);
  const [corporateRequiresProfile, setCorporateRequiresProfile] =
    useState(null);
  const [token, setToken] = useState(Cookies.get("token") || null);

  // Add fetchProfile function for use in other methods
  const fetchProfile = useCallback(async () => {
    const currentToken = Cookies.get("token");
    if (!currentToken) {
      setProfile(null);
      return null;
    }

    // Check user type from token to prevent corporate users from fetching candidate profile
    try {
      const decoded = jwtDecode(currentToken);
      if (decoded?.userType === "corporate") {
        return null;
      }
    } catch (decodeError) {
      console.warn("Failed to decode token:", decodeError);
    }

    try {
      const profileUrl = `${process.env.REACT_APP_API_URL}/api/profile`;
      const profileRes = await fetch(profileUrl, {
        headers: { Authorization: `Bearer ${currentToken}` },
      });

      if (profileRes.ok) {
        const profileData = await profileRes.json();
        const normalized = normalizeProfileData(profileData);

        setProfile(normalized);
        return normalized;
      } else {
        setProfile(null);
        return null;
      }
    } catch (e) {
      console.error("🔍 DEBUG: ProfileContext - fetchProfile error:", e);
      setProfile(null);
      return null;
    }
  }, [normalizeProfileData]);

  // Fetch corporate profile status
  const fetchCorporateProfileStatus = useCallback(async () => {
    const currentToken = Cookies.get("token");
    if (!currentToken) {
      setCorporateRequiresProfile(null);
      return null;
    }
    try {
      const statusUrl = `${process.env.REACT_APP_API_URL}/api/profile/corporate/status`;
      const statusRes = await fetch(statusUrl, {
        headers: { Authorization: `Bearer ${currentToken}` },
      });

      if (statusRes.ok) {
        const statusData = await statusRes.json();

        setCorporateRequiresProfile(statusData.requiresProfile || false);
        return statusData.requiresProfile || false;
      } else {
        setCorporateRequiresProfile(false);
        return false;
      }
    } catch (e) {
      console.error(
        "🔍 DEBUG: ProfileContext - fetchCorporateProfileStatus error:",
        e,
      );
      setCorporateRequiresProfile(false);
      return false;
    }
  }, []);

  // Fetch countries from backend
  const fetchCountries = useCallback(async () => {
    try {
      const currentToken = Cookies.get("token");
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/profile/countries`,
        {
          headers: currentToken
            ? { Authorization: `Bearer ${currentToken}` }
            : {},
        },
      );
      if (!response.ok) throw new Error("Failed to fetch countries");
      const data = await response.json();
      setCountries(data);
    } catch (error) {
      setCountries([]);
      console.error(
        "[PROFILE] fetchCountries: Error fetching countries:",
        error,
      );
    }
  }, []);

  useEffect(() => {
    const currentToken = Cookies.get("token");
    if (currentToken) {
      fetchCountries();
    }
  }, [fetchCountries, token]);

  const hasPendingUpdateVersion = useCallback(async () => {
    try {
      const currentToken = Cookies.get("token");
      if (!currentToken) return false;

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/profile/update-status`,
        {
          headers: {
            Authorization: `Bearer ${currentToken}`,
          },
        },
      );

      if (!response.ok) {
        return false;
      }

      const data = await response.json();
      return Boolean(data?.hasPendingVersion);
    } catch (error) {
      console.error("[PROFILE] hasPendingUpdateVersion error", error);
      return false;
    }
  }, []);

  // Sequential fetch: fetch profile, then review status if profile exists
  useEffect(() => {
    const currentToken = Cookies.get("token");
    if (!currentToken) {
      setLoading(false);
      return;
    }

    const fetchAll = async () => {
      setLoading(true);

      try {
        // Check user type from token
        try {
          const decoded = jwtDecode(currentToken);
          if (decoded?.userType === "corporate") {
            setLoading(false);
            return;
          }
        } catch (decodeError) {
          console.warn("Failed to decode token:", decodeError);
        }

        // Check localStorage first
        const existingTempData = localStorage.getItem("tempProfileData");
        let cachedTempData = null;

        if (existingTempData) {
          try {
            cachedTempData = JSON.parse(existingTempData);
            // Update tempProfileData state immediately with cached data
            setTempProfileData(cachedTempData);
          } catch (parseError) {
            console.warn("Failed to parse cached tempProfileData:", parseError);
          }
        }

        // Always fetch fresh profile data from server
        const profileUrl = `${process.env.REACT_APP_API_URL}/api/profile`;

        const profileRes = await fetch(profileUrl, {
          headers: { Authorization: `Bearer ${currentToken}` },
        });

        if (profileRes.status === 401 || profileRes.status === 403) {
          Cookies.remove("token");
          window.location.href = "/login";
          return;
        }

        let normalizedProfile = null;

        if (profileRes.status === 404) {
          // User doesn't have a profile yet - keep cached temp data if it exists
          setProfile(null);
          setHasExistingProfile(false);
          setIsEditingProfile(true);
        } else if (profileRes.ok) {
          const profileData = await profileRes.json();

          normalizedProfile = normalizeProfileData(profileData);
          console.log("normalizedProfile", normalizedProfile);
          // Merge with cached temp data if available
          if (cachedTempData) {
            normalizedProfile = {
              ...normalizedProfile,
              personal_info:
                normalizedProfile.personal_info || cachedTempData.personalInfo,
              career_details:
                normalizedProfile.career_details ||
                cachedTempData.careerDetails,
              preferred_roles:
                normalizedProfile.preferred_roles ||
                cachedTempData.preferredRoles,
              softSkills: normalizedProfile.softSkills?.length
                ? normalizedProfile.softSkills
                : cachedTempData.softSkills,
              languages: normalizedProfile.languages?.length
                ? normalizedProfile.languages
                : cachedTempData.languages,
            };
          }

          setProfile(normalizedProfile);
          setHasExistingProfile(true);
        } else {
          setProfile(null);
          setHasExistingProfile(false);
          setIsEditingProfile(true);
        }

        // Fetch review status
        const reviewUrl = `${process.env.REACT_APP_API_URL}/api/profile/review-status`;
        const reviewRes = await fetch(reviewUrl, {
          headers: { Authorization: `Bearer ${currentToken}` },
        });

        if (reviewRes.status === 401 || reviewRes.status === 403) {
          Cookies.remove("token");
          window.location.href = "/login";
          return;
        }

        if (reviewRes.ok) {
          const reviewData = await reviewRes.json();

          let pendingVersion = false;
          try {
            pendingVersion = await hasPendingUpdateVersion();
          } catch (pendingError) {
            console.warn(
              "[PROFILE] Failed to determine pending update status",
              pendingError,
            );
          }

          if (
            reviewData &&
            reviewData.isActive === 1 &&
            reviewData.review_status === "Under Review" &&
            !pendingVersion
          ) {
            reviewData.review_status = "Approved";
          }

          reviewData.hasPendingVersion = pendingVersion;
          setProfileReviewStatus(reviewData);

          // Initialize tempProfileData for Approved+Active users
          if (
            normalizedProfile &&
            reviewData &&
            reviewData.review_status === "Approved" &&
            (reviewData.isActive === 1 || reviewData.isActive === true) &&
            !reviewData.hasPendingVersion
          ) {
            const tempData = {
              personalInfo: normalizedProfile.personal_info,
              careerDetails: normalizedProfile.career_details,
              preferredRoles: normalizedProfile.preferred_roles,
              softSkills: normalizedProfile.softSkills || [],
              languages: normalizedProfile.languages || [],
              roleExpertise: normalizedProfile.roleExpertise,
            };

            localStorage.setItem("tempProfileData", JSON.stringify(tempData));
            setTempProfileData(tempData);
          }
        } else {
          setProfileReviewStatus(null);
        }

        setLoading(false);
      } catch (e) {
        console.error("🔍 DEBUG: ProfileContext - Error in fetchAll:", e);
        setProfile(null);
        setProfileReviewStatus(null);
        setLoading(false);
      }
    };

    fetchAll();
  }, [token, normalizeProfileData, hasPendingUpdateVersion]);

  // Listen for token changes
  useEffect(() => {
    const handleStorage = () => {
      setToken(Cookies.get("token"));
    };
    window.addEventListener("storage", handleStorage);

    const handleTokenUpdated = () => {
      setToken(Cookies.get("token"));
    };
    window.addEventListener("token-updated", handleTokenUpdated);

    return () => {
      window.removeEventListener("storage", handleStorage);
      window.removeEventListener("token-updated", handleTokenUpdated);
    };
  }, []);

  // Helper to persist tempProfileData to localStorage
  const persistTempProfileData = useCallback((data) => {
    const dataToSave = {
      personalInfo: data.personalInfo,
      careerDetails: data.careerDetails,
      preferredRoles: data.preferredRoles,
      softSkills: data.softSkills || [],
      languages: data.languages || [],
      roleExpertise: data.roleExpertise,
    };

    const stored = localStorage.getItem("tempProfileData");
    const current = stored ? JSON.parse(stored) : null;

    if (JSON.stringify(current) !== JSON.stringify(dataToSave)) {
      localStorage.setItem("tempProfileData", JSON.stringify(dataToSave));

      // Verify the save
      const savedData = localStorage.getItem("tempProfileData");
    }

    setTempProfileData(dataToSave);
  }, []);

  // Update temporary personal info
  const updateTempPersonalInfo = useCallback(
    (personalInfo) => {
      console.log("Personal info to save:", hasProfileChanges);
      persistTempProfileData({ ...tempProfileData, personalInfo });
      return { message: "Personal info saved temporarily" };
    },
    [tempProfileData, persistTempProfileData],
  );

  // Update temporary career details
  const updateTempCareerDetails = useCallback(
    (careerDetails) => {
      console.log("Career details to save:", hasProfileChanges);
      persistTempProfileData({ ...tempProfileData, careerDetails });
      return { message: "Career details saved temporarily" };
    },
    [tempProfileData, persistTempProfileData],
  );

  // Update temporary preferred roles
  const updateTempPreferredRoles = useCallback(
    (preferredRoles) => {
      console.log("Preferred roles to save:", hasProfileChanges);
      persistTempProfileData({ ...tempProfileData, preferredRoles });
      return { message: "Preferred roles saved temporarily" };
    },
    [tempProfileData, persistTempProfileData],
  );

  // Get merged profile data (temp + existing)
  const getMergedProfile = useCallback(() => {
    // If we have profile data, merge with temp data
    if (profile) {
      const merged = {
        ...profile,
        personal_info: tempProfileData.personalInfo || profile.personal_info,
        career_details: tempProfileData.careerDetails || profile.career_details,
        preferred_roles:
          tempProfileData.preferredRoles || profile.preferred_roles,
        softSkills: tempProfileData.softSkills?.length
          ? tempProfileData.softSkills
          : profile.softSkills || [],
        languages: tempProfileData.languages?.length
          ? tempProfileData.languages
          : profile.languages || [],
        roleExpertise: tempProfileData.roleExpertise || profile.roleExpertise,
      };
      return merged;
    }

    // If no profile but we have temp data, return temp data
    if (
      tempProfileData.personalInfo ||
      tempProfileData.careerDetails ||
      tempProfileData.preferredRoles
    ) {
      const tempAsProfile = {
        personal_info: tempProfileData.personalInfo,
        career_details: tempProfileData.careerDetails,
        preferred_roles: tempProfileData.preferredRoles,
        softSkills: tempProfileData.softSkills || [],
        languages: tempProfileData.languages || [],
        roleExpertise: tempProfileData.roleExpertise,
      };

      return tempAsProfile;
    }

    return null;
  }, [profile, tempProfileData]);

  // Save complete profile to database
  const saveCompleteProfile = useCallback(
    async (options = {}) => {
      try {
        const currentToken = Cookies.get("token");
        if (!currentToken) {
          throw new Error("No authentication token found");
        }

        const mergedData = getMergedProfile();

        // Check if we have any data to save
        const hasAnyData =
          mergedData ||
          tempProfileData.personalInfo ||
          tempProfileData.careerDetails ||
          tempProfileData.preferredRoles;

        if (!hasAnyData) {
          throw new Error(
            "No profile data available to save. Please complete your profile sections first.",
          );
        }

        // Don't send null data - use tempProfileData for new users
        const dataToSend = mergedData || {
          personal_info: tempProfileData.personalInfo,
          career_details: tempProfileData.careerDetails,
          preferred_roles: tempProfileData.preferredRoles?.roles,
          soft_skills: tempProfileData.preferredRoles?.softSkills,
          languages: tempProfileData.preferredRoles?.languages,
        };

        // Create FormData to handle file uploads
        const formData = new FormData();

        if (dataToSend.personal_info) {
          console.log("🔍 DEBUG: Adding personal_info to formData");
          formData.append(
            "personal_info",
            JSON.stringify(dataToSend.personal_info),
          );
        }

        const preferredRolesData = dataToSend.preferred_roles || {};

        const roles =
          preferredRolesData.roles || dataToSend.preferred_roles || [];
        const softSkills =
          dataToSend.soft_skills ||
          preferredRolesData.softSkills ||
          dataToSend.softSkills ||
          [];
        const languages =
          tempProfileData.preferredRoles?.languages &&
          tempProfileData.preferredRoles?.languages.length > 0
            ? tempProfileData.preferredRoles?.languages
            : dataToSend.languages || preferredRolesData.languages;

        // Add roles
        if (roles.length) {
          formData.append("preferred_roles", JSON.stringify(roles));
        }

        // Add soft skills
        if (softSkills.length) {
          formData.append("soft_skills", JSON.stringify(softSkills));
        }

        // Add languages
        if (languages.length) {
          formData.append("languages", JSON.stringify(languages));
        }
        // Add CV file if it exists in career_details
        // if (dataToSend.career_details && dataToSend.career_details.cv_file) {
        //   formData.append("cv", dataToSend.career_details.cv_file);
        // }

        if (dataToSend.career_details?.cv_file) {
          const cvFile = dataToSend.career_details.cv_file;

          if (cvFile instanceof File) {
            // ✅ Case 1: Direct File upload (newly selected by user)
            formData.append("cv", cvFile);
          } else if (cvFile?.base64 && cvFile?.name && cvFile?.type) {
            // ✅ Case 2: Reconstructed File from Base64 (stored version)
            try {
              const byteString = atob(cvFile.base64.split(",")[1]);
              const ab = new ArrayBuffer(byteString.length);
              const ia = new Uint8Array(ab);
              for (let i = 0; i < byteString.length; i++) {
                ia[i] = byteString.charCodeAt(i);
              }

              const blob = new Blob([ab], { type: cvFile.type });
              const reconstructedFile = new File([blob], cvFile.name, {
                type: cvFile.type,
              });
              formData.append("cv", reconstructedFile);
            } catch (err) {
              console.error(
                "⚠️ Failed to reconstruct CV file from base64:",
                err,
              );
            }
          }
        }

        if (dataToSend.career_details) {
          const { cv_file, ...careerDetailsWithoutFile } =
            dataToSend.career_details;
          formData.append(
            "career_details",
            JSON.stringify(careerDetailsWithoutFile),
          );
        }

        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/api/profile`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${currentToken}`,
            },
            body: formData,
          },
        );

        if (!response.ok) {
          const errorData = await response.text();
          console.error(
            "🔍 DEBUG: Server response error:",
            response.status,
            errorData,
          );
          let errorMessage;
          try {
            const parsedError = JSON.parse(errorData);
            errorMessage = parsedError.message || parsedError.error;
          } catch (e) {
            errorMessage = errorData || "Failed to save profile";
          }
          throw new Error(errorMessage);
        }

        const result = await response.json();
        console.log("🔍 DEBUG: Profile saved successfully:", result);

        // Refresh profile data from server
        await fetchProfile();

        return { message: "Profile saved successfully", profile: result };
      } catch (error) {
        console.error(
          "[PROFILE] saveCompleteProfile: Error saving profile:",
          error,
        );
        throw error;
      }
    },
    [getMergedProfile, fetchProfile],
  );

  // Update personal info in database
  const updatePersonalInfo = useCallback(async (personalInfo) => {
    try {
      const currentToken = Cookies.get("token");
      if (!currentToken) {
        throw new Error("No authentication token found");
      }

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/profile/personal-info`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${currentToken}`,
          },
          body: JSON.stringify(personalInfo),
        },
      );

      if (!response.ok) {
        const errorData = await response.text();
        let errorMessage;
        try {
          const parsedError = JSON.parse(errorData);
          errorMessage = parsedError.message;
        } catch (e) {
          errorMessage = errorData || "Failed to update personal info";
        }
        throw new Error(errorMessage);
      }

      const result = await response.json();

      setProfile((prev) =>
        prev
          ? { ...prev, personal_info: result.personal_info }
          : { personal_info: result.personal_info },
      );

      return {
        message: "Personal info updated successfully",
        personal_info: result.personal_info,
      };
    } catch (error) {
      console.error(
        "[PROFILE] updatePersonalInfo: Error updating personal info:",
        error,
      );
      throw error;
    }
  }, []);

  // Update career details in database
  const updateCareerDetails = useCallback(async (careerDetails) => {
    try {
      const currentToken = Cookies.get("token");
      if (!currentToken) {
        throw new Error("No authentication token found");
      }

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/profile/career-details`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${currentToken}`,
          },
          body: JSON.stringify(careerDetails),
        },
      );

      if (!response.ok) {
        const errorData = await response.text();
        let errorMessage;
        try {
          const parsedError = JSON.parse(errorData);
          errorMessage = parsedError.message;
        } catch (e) {
          errorMessage = errorData || "Failed to update career details";
        }
        throw new Error(errorMessage);
      }

      const result = await response.json();

      setProfile((prev) =>
        prev
          ? { ...prev, career_details: result.career_details }
          : { career_details: result.career_details },
      );

      return {
        message: "Career details updated successfully",
        career_details: result.career_details,
      };
    } catch (error) {
      console.error(
        "[PROFILE] updateCareerDetails: Error updating career details:",
        error,
      );
      throw error;
    }
  }, []);

  // Upload CV
  const uploadCV = useCallback(
    async (file) => {
      try {
        const currentToken = Cookies.get("token");
        if (!currentToken) {
          throw new Error("No authentication token found");
        }

        const formData = new FormData();
        formData.append("cv", file);

        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/api/profile/upload-cv`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${currentToken}`,
            },
            body: formData,
          },
        );

        const responseData = await response.text();

        if (!response.ok) {
          let errorMessage;
          try {
            const errorData = JSON.parse(responseData);
            errorMessage = errorData.message;
          } catch (e) {
            errorMessage = responseData || "Failed to upload CV";
          }
          throw new Error(errorMessage);
        }

        if (
          !profileReviewStatus ||
          profileReviewStatus.review_status !== "Under Review"
        ) {
          await fetchProfile();
        }

        return { message: "CV uploaded successfully" };
      } catch (error) {
        throw error;
      }
    },
    [profileReviewStatus, fetchProfile],
  );

  const downloadCV = useCallback(async () => {
    try {
      const currentToken = Cookies.get("token");
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/profile/download-cv`,
        {
          headers: {
            Authorization: `Bearer ${currentToken}`,
          },
        },
      );

      if (!response.ok) {
        throw new Error("Failed to download CV");
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "cv.pdf";
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      throw error;
    }
  }, []);

  const resetTempProfileData = useCallback(() => {
    const emptyData = {
      personalInfo: null,
      careerDetails: null,
      preferredRoles: null,
      roleExpertise: null,
      softSkills: [],
      languages: [],
    };
    setTempProfileData(emptyData);
    localStorage.removeItem("tempProfileData");
    localStorage.removeItem("tempRoleExpertise");
  }, []);

  const syncTempProfileDataFromStorage = useCallback(() => {
    try {
      const saved = localStorage.getItem("tempProfileData");

      if (!saved) {
        return null;
      }

      const parsed = JSON.parse(saved);

      const newTempProfileData = {
        personalInfo: parsed?.personalInfo || null,
        careerDetails: parsed?.careerDetails || null,
        preferredRoles: parsed?.preferredRoles || null,
        softSkills: parsed?.softSkills || [],
        languages: parsed?.languages || [],
        roleExpertise: parsed?.roleExpertise || null,
      };

      setTempProfileData(newTempProfileData);
      return newTempProfileData;
    } catch (error) {
      console.error(
        "🔍 DEBUG: ProfileContext - Error syncing temp data from localStorage:",
        error,
      );
      return null;
    }
  }, []);

  // Reset all profile context state - to be called on logout
  const resetProfileContext = useCallback(() => {
    setProfile(null);
    setProfileReviewStatus(null);
    setIsEditingProfile(false);
    setHasProfileChanges(false);
    setHasExistingProfile(false);
    setTempProfileData({
      personalInfo: null,
      careerDetails: null,
      preferredRoles: null,
      roleExpertise: null,
      softSkills: [],
      languages: [],
    });
    localStorage.removeItem("tempProfileData");
    localStorage.removeItem("tempRoleExpertise");
    localStorage.removeItem("profileReviewStatus");
    localStorage.removeItem("profileUpdateReviewStatus");
  }, []);

  // Helper booleans for UI logic
  const isUnderReview = profileReviewStatus?.review_status === "Under Review";
  const isUpdateReview = profileReviewStatus?.hasPendingVersion ? true : false;
  const isActiveBool = Boolean(profileReviewStatus?.isActive);
  const isApprovedNotActive =
    profileReviewStatus?.review_status === "Approved" && !isActiveBool;
  const isApprovedAndActive =
    profileReviewStatus?.review_status === "Approved" && isActiveBool;

  const isProfileReadOnly =
    isUnderReview || isApprovedNotActive || isUpdateReview;

  const showFullProfileFields = useMemo(
    () => getShowFullProfileFields(profile),
    [profile],
  );

  const value = useMemo(
    () => ({
      profile: getMergedProfile(),
      showFullProfileFields,
      loading,
      countries,
      functions,
      tempProfileData,
      updatePersonalInfo,
      updateTempPersonalInfo,
      updateCareerDetails,
      updateTempCareerDetails,
      updateTempPreferredRoles,
      saveCompleteProfile,
      uploadCV,
      downloadCV,
      resetTempProfileData,
      syncTempProfileDataFromStorage,
      resetProfileContext,
      profileReviewStatus,
      isProfileReadOnly,
      isUnderReview,
      isUpdateReview,
      isApprovedNotActive,
      isApprovedAndActive,
      hasPendingUpdateVersion,
      getMergedProfile,
      setProfile,
      setProfileReviewStatus,
      corporateRequiresProfile,
      fetchCorporateProfileStatus,
      setCorporateRequiresProfile,
      isEditingProfile,
      setIsEditingProfile,
      hasProfileChanges,
      setHasProfileChanges,
      hasExistingProfile,
      showFullProfileFields,
    }),
    [
      getMergedProfile,
      showFullProfileFields,
      loading,
      countries,
      functions,
      tempProfileData,
      updatePersonalInfo,
      updateTempPersonalInfo,
      updateCareerDetails,
      updateTempCareerDetails,
      updateTempPreferredRoles,
      saveCompleteProfile,
      uploadCV,
      downloadCV,
      resetTempProfileData,
      syncTempProfileDataFromStorage,
      resetProfileContext,
      profileReviewStatus,
      isProfileReadOnly,
      isUnderReview,
      isUpdateReview,
      isApprovedNotActive,
      isApprovedAndActive,
      hasPendingUpdateVersion,
      corporateRequiresProfile,
      fetchCorporateProfileStatus,
      isEditingProfile,
      hasProfileChanges,
      hasExistingProfile,
    ],
  );

  return (
    <ProfileContext.Provider value={value}>{children}</ProfileContext.Provider>
  );
};
